import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import { useParams } from 'react-router';

import Header from '../Header';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';

import AppendixList from './AppendixList';
import { GET_APPENDICES, GET_SELECTION_FIELDS } from './constants';
import NewAppendixDialog from './NewAppendixDialog';
import type { GetAppendices, GetLayoutTypes } from './types';

import { Empty, EmptyContent } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';

export function AppendicesSection({
  opportunityId,
  section,
  title,
  variant = 'embedded'
}: {
  opportunityId: string;
  section?: 'company' | 'general';
  title?: string;
  variant?: 'embedded' | 'page';
}) {
  const { data: data1, loading: loading1 } =
    useQuery<GetLayoutTypes>(GET_SELECTION_FIELDS);

  const {
    data: appendicesData,
    loading: loadingAppendices,
    refetch: refetchAppendices
  } = useQuery<GetAppendices>(GET_APPENDICES, {
    skip: !opportunityId,
    variables: { dealId: opportunityId }
  });

  const { companyAppendices, generalAppendices } = useMemo(() => {
    const all = appendicesData?.appendices ?? [];
    const titleFilter = title?.trim().toLowerCase();
    const filteredByTitle = titleFilter
      ? all.filter(a => {
          const fullTitle = a.title.trim().toLowerCase();
          const splitTitle =
            a.title.split(':')[1]?.trim().toLowerCase() ?? fullTitle;
          return fullTitle === titleFilter || splitTitle === titleFilter;
        })
      : all;
    return {
      companyAppendices: filteredByTitle.filter(
        a => a.appendixType === 'company'
      ),
      generalAppendices: filteredByTitle.filter(
        a => a.appendixType === 'general'
      )
    };
  }, [appendicesData, title]);

  if (loading1 || (loadingAppendices && !appendicesData)) {
    return variant === 'page' ? (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    ) : (
      <div className="grid place-content-center py-8">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (!data1) {
    return <div>{'Error'}</div>;
  }

  const handleRefetch = () => {
    void refetchAppendices();
  };

  const generalContent = (
    <div className="mt-4">
      {generalAppendices.length === 0 ? (
        <Empty>
          <EmptyContent>General appendices will be created automatically.</EmptyContent>
        </Empty>
      ) : (
        <AppendixList
          appendices={generalAppendices}
          data={data1}
          onSuccess={handleRefetch}
        />
      )}
    </div>
  );

  const companyContent = (
    <div className="mt-4">
      {companyAppendices.length === 0 ? (
        <Empty>
          <EmptyContent>
            <p className="text-muted-foreground mb-4">
              You currently have no appendices yet.
            </p>
            <NewAppendixDialog
              data={data1}
              dealOpportunityId={opportunityId}
              onSuccess={handleRefetch}
            />
          </EmptyContent>
        </Empty>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-end">
            <NewAppendixDialog
              data={data1}
              dealOpportunityId={opportunityId}
              onSuccess={handleRefetch}
            />
          </div>
          <AppendixList
            appendices={companyAppendices}
            data={data1}
            onSuccess={handleRefetch}
          />
        </div>
      )}
    </div>
  );

  const sectionFromTitle = title
    ? generalAppendices.length > 0 && companyAppendices.length === 0
      ? 'general'
      : companyAppendices.length > 0 && generalAppendices.length === 0
        ? 'company'
        : generalAppendices.length > 0
          ? 'general'
          : companyAppendices.length > 0
            ? 'company'
            : 'general'
    : undefined;

  const effectiveSection = section ?? sectionFromTitle;

  if (effectiveSection) {
    return (
      <div className="min-w-0 space-y-2.5 px-5 pt-5 pb-16.5">
        {effectiveSection === 'general' ? generalContent : companyContent}
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-2.5 px-5 pt-5 pb-16.5">
      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General Appendix</TabsTrigger>
          <TabsTrigger value="company">Company Appendix</TabsTrigger>
        </TabsList>

        <TabsContent value="general">{generalContent}</TabsContent>

        <TabsContent value="company">{companyContent}</TabsContent>
      </Tabs>
    </div>
  );
}

const InvestmentsDirectIdAppendices = () => {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    throw new Error();
  }

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <AppendicesSection
          opportunityId={id}
          variant="page"
        />
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectIdAppendices;
